import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';
import { skipToken } from '@tanstack/react-query';
import { captureException } from 'r/utils';
import {
  fetchApiRaw,
  GET_ANALYSES_CMS,
  GET_ANALYSES,
} from 'r/utils/fetchApiRaw';
import type { Analyse } from 'r/types';
import type { MultiSelectOption } from 'r/ui/MultiSelectComboBox';
import {
  buildFilterString,
  serializeSort,
  type FilterCondition,
} from './utils';
import { issuersKolsAtom } from './kols';
import { hlFilteredPairsAtom } from './pairs';

const hlFilteredPairsLoadableAtom = loadable(hlFilteredPairsAtom);

const ANALYSIS_MANAGER_API_AUTHOR_ID = '485b4d0f-575b-48c5-b02f-f18c8e57dba7';

export const analysisManagerFiltersAtom = atom({
  searchQuery: '',
  filterType: 'all' as 'all' | 'api' | 'manual',
  showDeletedOnly: false,
  authorFilterMode: 'all' as 'all' | 'kols',
  excludedAuthors: [] as string[],
  selectedPair: null as MultiSelectOption[] | null,
  sortBy: 'createdAt_desc',
  startDate: '',
  endDate: '',
  dateFilterType: 'all' as 'all' | 'createdAt' | 'tweetDate',
  currentPage: 1,
  pageSize: 12,
});

export const analysisManagerQueryAtom = atomWithQuery((get) => {
  const filters = get(analysisManagerFiltersAtom);
  const issuersKols = get(issuersKolsAtom).data;
  const hlPairsLoadable = get(hlFilteredPairsLoadableAtom);
  const hlPairs =
    hlPairsLoadable.state === 'hasData' ? hlPairsLoadable.data : null;
  const hlSymbols =
    hlPairs
      ?.map((p) => p.symbol)
      .sort()
      .join(',') ?? 'loading';

  const kolIDs =
    filters.authorFilterMode === 'kols' ?
      (issuersKols
        ?.map((k) => k.twitterID)
        .filter((id): id is string => id != null) ?? null)
    : null;

  return {
    queryKey: [
      'cms-analyses',
      filters.searchQuery,
      filters.currentPage,
      filters.pageSize,
      filters.filterType,
      filters.showDeletedOnly,
      filters.sortBy,
      filters.authorFilterMode,
      kolIDs?.sort().join(',') ?? 'none',
      [...filters.excludedAuthors].sort().join(',') || 'none',
      filters.selectedPair
        ?.map((p) => p.id)
        .sort()
        .join(',') || 'none',
      filters.startDate,
      filters.endDate,
      filters.dateFilterType,
      hlSymbols,
    ],
    queryFn:
      (filters.authorFilterMode === 'kols' && !issuersKols) || !hlPairs ?
        skipToken
      : async () => {
          try {
            const filterConditions: Record<string, FilterCondition> = {};

            if (filters.searchQuery.trim()) {
              const searchPattern = `%${filters.searchQuery.trim()}%`;
              filterConditions.$or = [
                { tweetText: { $ilike: searchPattern } },
                { authorName: { $ilike: searchPattern } },
                { authorUsername: { $ilike: searchPattern } },
              ];
            }

            if (filters.filterType === 'api') {
              filterConditions.analysisAuthorId =
                ANALYSIS_MANAGER_API_AUTHOR_ID;
            } else if (filters.filterType === 'manual') {
              filterConditions.analysisAuthorId = {
                $ne: ANALYSIS_MANAGER_API_AUTHOR_ID,
              };
            }

            if (filters.showDeletedOnly) {
              filterConditions.deletedAt = { $ne: null };
            } else {
              filterConditions.deletedAt = null;
            }

            const queryParams: Record<string, string> = {
              skip: String((filters.currentPage - 1) * filters.pageSize),
              take: String(filters.pageSize),
            };

            const lastUnderscore = filters.sortBy.lastIndexOf('_');
            const sortField = filters.sortBy.slice(0, lastUnderscore);
            const sortDirection = filters.sortBy
              .slice(lastUnderscore + 1)
              .toUpperCase() as 'ASC' | 'DESC';
            queryParams.sort = serializeSort(sortField, sortDirection);

            if (kolIDs && kolIDs.length > 0) {
              // KOLs only: build inclusion array, subtract excluded authors
              const included =
                filters.excludedAuthors.length > 0 ?
                  kolIDs.filter((id) => !filters.excludedAuthors.includes(id))
                : kolIDs;
              if (included.length > 0) {
                filterConditions.authorID = included;
              }
            } else if (filters.excludedAuthors.length > 0) {
              // All mode with exclusions: exclude specific twitterIDs
              if (filters.excludedAuthors.length === 1) {
                filterConditions.authorID = { $ne: filters.excludedAuthors[0] };
              } else {
                filterConditions.authorID = {
                  $notIn: filters.excludedAuthors,
                } as FilterCondition;
              }
            }
            if (filters.selectedPair && filters.selectedPair.length > 0) {
              filterConditions.pair = filters.selectedPair.map(
                (pair) => pair.id,
              );
            } else if (hlPairs && hlPairs.length > 0) {
              filterConditions.pair = hlPairs.map((p) => p.symbol);
            }

            if (filters.dateFilterType !== 'all') {
              const dateFilter: Record<string, string> = {};
              if (filters.startDate) dateFilter.$gt = filters.startDate;
              if (filters.endDate) dateFilter.$lt = filters.endDate;

              if (Object.keys(dateFilter).length > 0) {
                filterConditions[filters.dateFilterType] = dateFilter;
              }
            }

            if (Object.keys(filterConditions).length > 0) {
              queryParams.filter = encodeURIComponent(
                buildFilterString(filterConditions),
              );
            }

            const response = await fetchApiRaw(GET_ANALYSES_CMS, {
              queryParams,
            });

            const list =
              Array.isArray(response.data) ? (response.data as Analyse[]) : [];

            return {
              data: list,
              total: response.total || 0,
            };
          } catch (e) {
            captureException(e);
            return { data: [], total: 0 };
          }
        },
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchOnWindowFocus: false, // Disable aggressive refetching
  };
});

export const analysisManagerSanitizerQueryAtom = atomWithQuery((get) => {
  const mainQuery = get(analysisManagerQueryAtom);
  const filters = get(analysisManagerFiltersAtom);
  const analyses = mainQuery.data?.data as Analyse[] | undefined;

  // Create stable query key from tweetIDs
  const tweetIDsKey =
    analyses
      ?.map((item) => item.tweetID)
      .sort()
      .join(',') || '';

  return {
    queryKey: ['play-analyses-sanitizer', tweetIDsKey, filters.pageSize],
    queryFn:
      !analyses || analyses.length === 0 ?
        skipToken
      : async () => {
          try {
            const viewTweets = analyses
              .map((item) => `"${item.tweetID}"`)
              .join(',');

            const queryParams = {
              take: String(filters.pageSize * 5),
              filter: `"tweetID":[${viewTweets}]`,
            };

            const response = await fetchApiRaw(GET_ANALYSES, { queryParams });

            return {
              data:
                Array.isArray(response.data) ?
                  (response.data as Analyse[])
                : [],
            };
          } catch (e) {
            captureException(e);
            return { data: [] };
          }
        },
    enabled: !!analyses && analyses.length > 0,
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  };
});
